import {inject, Injectable} from "@angular/core";
import {BehaviorSubject, catchError, concat, finalize, map, Observable, of} from "rxjs";
import {HttpClient} from "@angular/common/http";
import DataResponse from "./DataResponse";
import RelationContactStore from "./Stores/relation-contact.store";
import LocalStorageService from "./LocalStorage.service";
import Relation from "../model/relation.model";
import RelationContact from "../model/relation-contact.model";
import Subscription from "../model/subscription.model";
import * as Sentry from "@sentry/angular";
import {Router} from "@angular/router";
import ErrorResponse, {LoginResult} from "../interfaces/Response.interface";
import {UserDto} from "../interfaces/User.interface";
import {deepMergeObjects} from "../functions/deepMergeObjects.function";
import {sortSubscriptions} from "../functions/sortSubscriptions.function";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly router: Router = inject(Router);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly userStore: RelationContactStore = inject(RelationContactStore);
  private readonly localStorageService: LocalStorageService = inject(LocalStorageService);

  private user: BehaviorSubject<UserDto> = new BehaviorSubject<UserDto>({
    user: new RelationContact()
  });
  private isLoggingIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isLoggingOut: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private loginResult: LoginResult = {status: false, error: null};
  private defaultIncludes: string = 'relations.subscriptions.products.discount_lines,relations.relation_contacts,relations.tickets.messages.relation_contact,relations.invoices.lines,surveys';

  constructor() {
    this.user.subscribe((user: UserDto) => {
      Sentry.setUser({
        id: user?.user?.id,
        username: user?.user?.name,
        email: user?.user?.email,
      });
    });
  }

  public login<T>(endpoint: string, loginObject: DataResponse<T>): Observable<LoginResult> {
    this.isLoggingIn.next(true);
    return this.http.post(endpoint + "?include=" + this.defaultIncludes, loginObject).pipe(
      map((response: object): LoginResult => {
        const responseObj: DataResponse<RelationContact> = DataResponse.fromResponse<RelationContact>(response);
        this.setUser(responseObj.data);

        if (responseObj.data) {
          this.userStore.add(responseObj.data);
        }
        this.loginResult = {status: true, error: null} as LoginResult;
        return this.loginResult;
      }),
      catchError((err: ErrorResponse): Observable<LoginResult> => {
        return of({
          status: false,
          error: err
        } as LoginResult);
      }),
      finalize(() => this.isLoggingIn.next(false))
    )
  }

  public isLoggedIn(forced: boolean = false): Observable<boolean> {
    if (this.loginResult?.status && !forced) {
      return of(true);
    }

    return this.http.get("/current?include=" + this.defaultIncludes).pipe(
      map((response: object) => {
        const result: DataResponse<RelationContact> = response as DataResponse<RelationContact>;
        if (result.data) {
          this.setUser(result.data);
          this.userStore.add(result.data);
          this.loginResult = {status: true, error: null} as LoginResult;
        }

        return result.data !== null;
      }),
      catchError(() => {
        this.loginResult = {status: false, error: null} as LoginResult;
        return of(false);
      })
    );
  }

  public logout(redirectUrl: string | null = null): Observable<boolean> {
    this.isLoggingOut.next(true);

    return this.http.post("/logout", null).pipe(
      map(() => true),
      catchError(() => of(false)),
      finalize(() => {
        this.registerLogout();
        this.router.navigate(['/login'], {
          queryParams: {'redirect-url': redirectUrl}
        });
      })
    );
  }

  public registerLogout(): void {
    this.loginResult = {status: false, error: null} as LoginResult;
    this.isLoggingOut.next(false);
    this.user.next({
      user: null,
    });
    this.localStorageService.clear();
  }

  public getUser(relations: string[] = []): Observable<RelationContact | null> {
    const includeParam: string = relations.length ? relations.join(',') : '';

    return concat(
      of(this.user.getValue()?.user ?? null), // Emit cached value first
      this.http.get<DataResponse<RelationContact>>(`/current`, {
        params: {include: includeParam}
      }).pipe(
        map((response: DataResponse<RelationContact>) => {
          return this.setUser(response.data);
        }),
        catchError(() => {
          return of(null);
        })
      )
    );
  }

  public setRelation(relation: Relation): void {
    this.localStorageService.set('selectedRelation', relation.id.toString());
  }

  public getActiveRelation(): Observable<Relation | null> {
    return this.user.pipe(
      map((data: UserDto) => {
        if (data.user === null) {
          return null;
        }

        const selectedRelationId: string | null = this.localStorageService.get('selectedRelation');

        if (!selectedRelationId) {
          return  null;
        }

        return data.user?.relations.find((relation: Relation) => relation.id === parseInt(selectedRelationId)) || null;
      })
    );
  }

  private setUser(newData: RelationContact | null): RelationContact | null {
    if (!newData) {
      return null;
    }

    const currentUserData: RelationContact = deepMergeObjects(this.user.getValue().user, newData) as unknown as RelationContact;

    const payload: UserDto = {
      user: currentUserData
    } as UserDto;

    this.user.next(payload);
    return currentUserData;
  }

  public reset(email: string): Observable<boolean> {
    return this.http.post('/auth/forgotten', {
      email: email
    }).pipe(
      map(() => true),
    );
  }

  public setSelectedSubscription(subscription: Subscription): void {
    this.localStorageService.set('selectedSubscription', subscription.id.toString());
  }

  public getSelectedSubscription(): Observable<Subscription | null> {
    return this.getActiveRelation().pipe(
      map((relation: Relation | null) => {
        if (!relation || !relation.subscriptions) {
          return null;
        }

        const sortedSubscriptions: Subscription[] = sortSubscriptions(relation.subscriptions);

        const selectedSubscriptionId: string | null = this.localStorageService.get('selectedSubscription');
        if (!selectedSubscriptionId) {
          //no selected subscription
          return null;
        }

        return sortedSubscriptions.find((subscription: Subscription) => subscription.id === parseInt(selectedSubscriptionId)) || null;
      })
    );
  }

}
